import React, { useState, useEffect } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { executeAgent } from '../../services/agentService';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { Step } from '../../types';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentInput, 
  AgentSelect,
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const IllustrationGenerator: React.FC = () => {
  const [sermonTheme, setSermonTheme] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [illustrationMode, setIllustrationMode] = useState('extrair-existentes');
  const [searchSource, setSearchSource] = useState('escrituras');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  useEffect(() => {
    if (!isLoading || startTime === null) return;
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoading, startTime]);

  const illustrationModes = [
    { value: 'extrair-existentes', label: 'Extrair Ilustrações Existentes' },
    { value: 'criar-novas', label: 'Criar Novas Ilustrações' },
  ];

  const searchSources = [
    { value: 'escrituras', label: 'Nas Escrituras' },
    { value: 'judaismo-classico', label: 'No Judaísmo Clássico' },
    { value: 'historia-igreja', label: 'Na História da Igreja' },
    { value: 'hinodia-crista', label: 'Na Hinódia Cristã' },
    { value: 'filosofia', label: 'Na Filosofia' },
    { value: 'historia-mundial', label: 'Na História Mundial' },
    { value: 'literatura-universal', label: 'Na Literatura Universal' },
    { value: 'filmes-midia', label: 'Nos Filmes e Mídia' },
    { value: 'temas-atualidade', label: 'Nos Temas da Atualidade' },
    { value: 'todas', label: 'Em todas as Fontes' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sermonTheme.trim()) {
      setError('Por favor, insira o tema do sermão ou estudo.');
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(sermonTheme, targetAudience)) {
      setShowSecurityModal(true);
      return;
    }

    setIsLoading(true);
    setError('');
    setResult('');
    setHasStarted(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSteps([{ id: '1', title: 'Gerando ilustrações...', status: 'running' }]);

    try {
      const parameters: Record<string, any> = {
        prompt: `Gere analogias e histórias que tornem a mensagem bíblica clara, viva e memorável sobre o tema: ${sermonTheme}`,
        tema: sermonTheme,
        modo_ilustracao: illustrationMode,
        onde_buscar: searchSource
      };

      if (targetAudience.trim()) {
        parameters.publico_alvo = targetAudience;
      }

      const data = await executeAgent('gerar-ilustracoes', parameters);
      setResult(data.text);
      setSteps([{ id: '1', title: 'Ilustrações geradas', status: 'success' }]);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao executar o agente';
      setError(`Erro: ${errorMessage}`);
      setSteps([{ id: '1', title: 'Erro ao gerar ilustrações', status: 'error', error: errorMessage }]);
      console.error('Erro ao executar agente:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSermonTheme('');
    setTargetAudience('');
    setIllustrationMode('extrair-existentes');
    setSearchSource('escrituras');
    setResult('');
    setError('');
    setSteps([]);
    setHasStarted(false);
  };

  return (
    <AgentPageLayout
      icon={<SparklesIcon className="w-6 h-6 text-secondary" />}
      title="Gerar Ilustrações"
      subtitle="Gere analogias e histórias que tornem a mensagem bíblica clara, viva e memorável."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <AgentFormGroup>
            <AgentLabel htmlFor="sermonTheme">Tema do Sermão/Estudo</AgentLabel>
            <AgentInput
              id="sermonTheme"
              type="text"
              value={sermonTheme}
              onChange={(e) => setSermonTheme(e.target.value)}
              placeholder="Ex: Perdão, Fé, Amor ao próximo..."
              disabled={isLoading}
            />
          </AgentFormGroup>

          <AgentFormGroup>
            <AgentLabel htmlFor="targetAudience">Público-Alvo</AgentLabel>
            <AgentInput
              id="targetAudience"
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Ex: Jovens, Casais, Líderes..."
              disabled={isLoading}
            />
          </AgentFormGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AgentFormGroup>
              <AgentLabel htmlFor="illustrationMode">Modo de Ilustração</AgentLabel>
              <AgentSelect
                id="illustrationMode"
                value={illustrationMode}
                onChange={(e) => setIllustrationMode(e.target.value)}
                disabled={isLoading}
              >
                {illustrationModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="searchSource">Onde buscar Ilustrações</AgentLabel>
              <AgentSelect
                id="searchSource"
                value={searchSource}
                onChange={(e) => setSearchSource(e.target.value)}
                disabled={isLoading}
              >
                {searchSources.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>
          </div>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Gerando Ilustrações...</span>
                </>
              ) : (
                <>
                  <SparklesIcon className="w-5 h-5" />
                  <span>Gerar Ilustrações</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} />}

        {hasStarted && (
          <section className="mt-8">
            <div className="bg-card border border-border rounded-lg p-6">
              {isLoading && (
                <div className="flex flex-col items-center justify-center gap-4 py-8">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">😇</span>
                    <h3 className="text-lg font-semibold text-foreground">
                      Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                    </h3>
                  </div>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                  {steps.length > 0 && steps[0]?.title && (
                    <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                  )}
                  {elapsedTime > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Tempo de execução: {formatElapsedTime(elapsedTime)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {result && (
          <AgentResult title="Ilustrações Geradas">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {result}
            </ReactMarkdown>
            <AgentDisclaimer />
            <div className="mt-6 flex items-center justify-end gap-3">
              <AgentOutputActions
                content={result}
                agentName="Gerar Ilustrações"
                agentCode="gerar-ilustracoes"
                reference={sermonTheme || 'Ilustrações'}
              />
              <AgentButton
                type="button"
                onClick={handleReset}
                variant="secondary"
                aria-label="Reiniciar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                <span>Reiniciar</span>
              </AgentButton>
            </div>
          </AgentResult>
        )}
      </AgentFormSection>

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
    </AgentPageLayout>
  );
};

export default IllustrationGenerator;
